import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { orderBy } from 'lodash'

import { AnalyticsValue } from './utils/types'
import { AnalyticsField, DuckDBSchemaType, FilterField } from '@types'

import { ANALYTICS_CAPTIONS } from '../utils/const'
import { ASSET_FIELD } from '@/const'

import { getCurrentDate } from '@/helpers/dates'
import { getFilterTags, getFilterValues } from '../utils/helpers'

import useAnalyticsStore from '../index'
import { useTagsBunchStore } from '@/store/tags/bunch'

import { DuckDBService } from '@/services/duckdb'

export const TABLE_NAME = 'analytics_value'

const TABLE_SCHEMA: Record<string, DuckDBSchemaType> = {
  [AnalyticsField.DATE]: 'DATE',
  [AnalyticsField.ACCOUNT_ID]: 'VARCHAR',
  [AnalyticsField.ASSET_ID]: 'VARCHAR',
  [AnalyticsField.TAGS]: 'VARCHAR[]',
  [AnalyticsField.AMOUNT]: 'DOUBLE',
  [AnalyticsField.D_AMOUNT]: 'DOUBLE',
  [AnalyticsField.POSITION_CLOSE]: 'DOUBLE',
  [AnalyticsField.POSITION_COST]: 'DOUBLE',
  [AnalyticsField.POSITION_GAIN]: 'DOUBLE',
  [AnalyticsField.D_POSITION_CLOSE]: 'DOUBLE',
  [AnalyticsField.D_POSITION_COST]: 'DOUBLE',
  [AnalyticsField.D_POSITION_GAIN]: 'DOUBLE',
}

export default defineStore('analytics-value', () => {
  // INIT
  const duckdbInstance = DuckDBService.instance()

  const loading = ref(false)
  const deprecated = ref<boolean>()
  const trigger = ref(0)

  const datesRange = ref<string[]>([])

  // GETTERS
  const isInit = computed(() => deprecated.value !== undefined)

  const getDatesRange = computed(() => datesRange.value)

  // SETTERS
  const setList = async (data: {
    value_view: Record<string, AnalyticsValue[]>;
  }) => {
    if (deprecated.value === false) return
    if (!data.value_view) return
    deprecated.value = false
    datesRange.value = Object.keys(data.value_view ?? {})
    await duckdbInstance.createTable(TABLE_NAME, TABLE_SCHEMA)
    await Promise.all(
      Object.values(data.value_view ?? {}).map(values =>
        duckdbInstance.pushData(TABLE_NAME, values, TABLE_SCHEMA),
      ),
    )
    trigger.value++
  }

  // ACTIONS
  const clear = () => {
    datesRange.value = []
    loading.value = false
    deprecated.value = undefined
    trigger.value = 0
  }

  return {
    isLoading: loading,
    isDeprecated: deprecated,

    isInit,
    trigger,

    getDatesRange,

    setList,

    clear,
  }
})

type UseAnalyticsValueFiltersOptions = {
  scoped?: boolean
  withDate?: boolean
  withAmount?: boolean
  withPosition?: boolean
  withDAmount?: boolean
  withDPosition?: boolean
}

export const useAnalyticsValueFilters = ({
  scoped = false,
  withDate = false,
  withAmount = false,
  withPosition = false,
  withDAmount = false,
  withDPosition = false,
}: UseAnalyticsValueFiltersOptions = {}) => {
  const analyticsStore = useAnalyticsStore()
  const tagsBunchStore = useTagsBunchStore()

  const analyticsDate = computed(
    () => analyticsStore.getDate || getCurrentDate({}),
  )

  const getValues = getFilterValues(
    TABLE_NAME,
    analyticsDate,
    scoped,
    tagsBunchStore,
  )
  const getTags = getFilterTags(TABLE_NAME, analyticsDate, tagsBunchStore, scoped)

  const filters = computed(() => {
    const result: FilterField[] = []
    if (withDate) {
      result.push({
        name: AnalyticsField.DATE,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.DATE],
        isDate: true,
      })
    }
    result.push(
      {
        name: AnalyticsField.ACCOUNT_NAME,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.ACCOUNT],
        getter: context => getValues(AnalyticsField.ACCOUNT_ID, undefined, context),
      },
      {
        name: AnalyticsField.ACCOUNT_TYPE,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.ACCOUNT_TYPE],
        getter: context => getValues(AnalyticsField.ACCOUNT_ID, ASSET_FIELD.TYPE, context),
      },
      {
        name: AnalyticsField.ASSET_NAME,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.ASSET],
        getter: context => getValues(AnalyticsField.ASSET_ID, undefined, context),
      },
      {
        name: AnalyticsField.ASSET_TYPE,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.ASSET_TYPE],
        getter: context => getValues(AnalyticsField.ASSET_ID, ASSET_FIELD.TYPE, context),
      },
    )
    if (withAmount) {
      result.push({
        name: AnalyticsField.AMOUNT,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.AMOUNT],
        isAmount: true,
      })
    }
    if (withPosition) {
      result.push({
        name: AnalyticsField.POSITION_COST,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.POSITION_COST],
        isAmount: true,
      })
      result.push({
        name: AnalyticsField.POSITION_CLOSE,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.POSITION_CLOSE],
        isAmount: true,
      })
      result.push({
        name: AnalyticsField.POSITION_GAIN,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.POSITION_GAIN],
        isAmount: true,
      })
    }
    if (withDAmount) {
      result.push({
        name: AnalyticsField.D_AMOUNT,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.D_AMOUNT],
        isAmount: true,
      })
    }
    if (withDPosition) {
      result.push({
        name: AnalyticsField.D_POSITION_COST,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.D_POSITION_COST],
        isAmount: true,
      })
      result.push({
        name: AnalyticsField.D_POSITION_CLOSE,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.D_POSITION_CLOSE],
        isAmount: true,
      })
      result.push({
        name: AnalyticsField.D_POSITION_GAIN,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.D_POSITION_GAIN],
        isAmount: true,
      })
    }
    tagsBunchStore.getMetricTags?.forEach(name => {
      result.push({
        name,
        caption: name,
        getter: context => getTags(name, context),
      })
    })
    return orderBy(result, 'caption')
  })

  return {
    filters,
  }
}
